/**
 * Read-only lockdown for the published demo.
 *
 * The demo is a recorded walkthrough: the transcript is fixed, so anything that
 * would compose a new turn, export a file, or open a settings surface has
 * nothing real behind it. Rather than leave those controls live and let them
 * fail quietly, they are disabled here and explain themselves when clicked —
 * a dead button that says nothing is what makes a demo feel broken.
 *
 * Two halves:
 *  - The components that already model a read-only state use it (the follow-up
 *    chips render as static pills; the composer renders disabled). That is what
 *    removes the hover affordance, so nothing *looks* clickable.
 *  - Everything else is caught here, at the document's capture phase, because
 *    the alternative is threading a flag through a dozen unrelated components
 *    on a branch whose whole point is to stay a thin layer over the real app.
 */

const LOCKED_SELECTORS = [
  // Composing a turn.
  '[data-testid="composer-input"]',
  '[aria-label="Message input"]',
  '[aria-label="Send message"]',
  '[aria-label="Suggested prompts"]',
  '[aria-label="Stop current request"]',
  // The recorded follow-up chips (rendered as static pills, see Message.tsx).
  '[data-demo-followup]',
  // Identity, configuration and account surfaces.
  '[aria-label^="Current patient"]',
  '[aria-label="Workspace settings"]',
  '[aria-label="Account menu"]',
  // Anything that hands over a file.
  'a[download]',
].join(',');

/** Buttons the app labels only by their text: exports and refetches. */
const LOCKED_LABELS = new Set(['csv', 'png', 'download', 'refresh', 'download pdf', 'download csv']);

function isLocked(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest(LOCKED_SELECTORS)) return true;
  const button = target.closest('button, a');
  if (!button) return false;
  const label = (button.getAttribute('aria-label') || button.textContent || '')
    .trim()
    .toLowerCase();
  return LOCKED_LABELS.has(label);
}

let lastNotice = 0;

/** One notice per gesture, not one per event — a click fires several. */
function explain(): void {
  const now = Date.now();
  if (now - lastNotice < 1200) return;
  lastNotice = now;
  void import('sonner').then(({ toast }) => {
    toast('This is a demo', {
      description: 'A recorded walkthrough of the clinician panel — this control is inactive here.',
    });
  });
}

let installed = false;

export function installDemoReadOnly(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  // Capture phase, so the block lands before React's own handlers run.
  for (const type of ['pointerdown', 'mousedown', 'click', 'dblclick'] as const) {
    document.addEventListener(
      type,
      (event) => {
        if (!isLocked(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        if (type === 'click') explain();
      },
      true,
    );
  }

  // Keyboard reaches the same controls; Tab is deliberately still allowed so
  // the page stays navigable for anyone reading it with a screen reader.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Tab') return;
      if (!isLocked(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Enter' || event.key === ' ') explain();
    },
    true,
  );

  const style = document.createElement('style');
  style.dataset.demoReadonly = 'true';
  style.textContent = `
    ${LOCKED_SELECTORS} { cursor: not-allowed !important; }
    [data-demo-followup] { cursor: not-allowed; }
  `;
  document.head.appendChild(style);
}

/** True when this build is the published, recorded demo.
 *
 * Reads import.meta.env through a cast: this module is also compiled by the
 * node/tooling tsconfig, which has no vite/client types. Vite still replaces
 * the expression statically at build time. */
export function isDemoReadOnly(): boolean {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return String(env?.VITE_DEMO_READONLY ?? '').trim().toLowerCase() === 'true';
}
